<template>
  <div class="juou-card">
    <HpsfirProTable
      ref="tableRef"
      export-file-name="角色管理"
      title="角色管理"
      :paginations="{ pageSize: 10 }"
      :columns="columns"
      :get-data="getData"
      :on-add="addFun"
      :on-delete="deleteFun"
      :on-edit="updateFun"
      :loading="loading"
      :is-dangder-delete="true"
      :format-dangder-delete-info="formatDeleteInfo"
      row-key="id"
    />
  </div>
</template>

<script lang="ts" setup>
import { ElMessage } from 'element-plus';
import { ref } from 'vue';
import {
  useBaseAsync,
  type HpsfirProTableColumn,
  type HpsfirProTableData,
  type HpsfirProTableFilter,
  type HpsfirProTableGetDataFunction,
} from 'hpsfir-managebase-ui';
import { type BaseMenuAdminVo } from '@/api';
import { useApi } from '@/hooks/useApi';
import { normalizeBizTableColumns } from '@/utils/tableColumns';
import type { BusinessRoleAdminVo } from '@/api/businessRole';

type MenuOption = {
  value: number;
  label: string;
  children?: MenuOption[];
};

type MenuRow = BaseMenuAdminVo & {
  children?: MenuRow[];
};

const columns = ref<HpsfirProTableColumn[]>(normalizeBizTableColumns([]));
const tableRef = ref<any>();
const loading = ref(false);
const menuOptions = ref<MenuOption[]>([]);
const menuLabelMap = ref(new Map<number, string>());

const cleanConditions = (conditions: Record<string, any>) => Object.entries(conditions || {}).reduce((res, [key, val]) => {
  if (val === null || val === undefined || val === '') {
    return res;
  }
  res[key] = val;
  return res;
}, {} as Record<string, any>);

const toTree = (menus: MenuRow[], parentId?: number): MenuRow[] => menus
  .filter((item) => item.parentId === parentId)
  .map((item) => {
    const row: MenuRow = { ...item };
    const children = toTree(menus, row.id);
    row.children = children.length ? children : undefined;
    return row;
  });

const toMenuOptions = (menus: MenuRow[]): MenuOption[] => menus.map((item) => {
  if (item.id) {
    menuLabelMap.value.set(item.id, item.title);
  }
  return {
    value: item.id!,
    label: item.title,
    children: item.children?.length ? toMenuOptions(item.children) : undefined,
  };
});

const renderMenuIds = (row: BusinessRoleAdminVo) => {
  if (!row.menuIds?.length) {
    return '-';
  }
  return row.menuIds.map((id) => menuLabelMap.value.get(id) || id).join('、');
};

const buildColumns = () => {
  columns.value = normalizeBizTableColumns([
    {
      name: 'name', label: '角色名称', type: 'string', isCondition: true,
      rule: [{ required: true }],
    },
    {
      name: 'remark', label: '备注', type: 'string',
    },
    {
      name: 'menuIds', label: '菜单权限', type: 'treeselect', multiple: true,
      options: menuOptions.value, rule: [{ required: true }],
      render: ({ row }: { row: BusinessRoleAdminVo }) => renderMenuIds(row),
    },
  ]);
};

const init = useBaseAsync(async() => {
  const res = await useApi().businessMenuApi.listMenus({ conditions: {} });
  const tree = toTree((res.data.data || []) as MenuRow[]);
  menuOptions.value = toMenuOptions(tree);
  buildColumns();
});

const getData = useBaseAsync<HpsfirProTableGetDataFunction>(async(filter: HpsfirProTableFilter) => {
  try {
    loading.value = true;
    const res = await useApi().businessRoleApi.pageBusinessRole({
      conditions: cleanConditions(filter.conditions),
      pageQuery: {
        page: filter.paginations.page - 1,
        pageSize: filter.paginations.pageSize,
      },
    });
    return {
      total: res.data.data?.total || 0,
      data: res.data.data?.data || [],
    } as HpsfirProTableData;
  } finally {
    loading.value = false;
  }
});

const formatDeleteInfo = (row: BusinessRoleAdminVo) => `您正在删除角色「${row.name}」，绑定该角色的账号将解除角色并同步清空对应用户菜单权限`;

const addFun = useBaseAsync(async(data: BusinessRoleAdminVo) => {
  await useApi().businessRoleApi.addBusinessRole(data);
  ElMessage.success('添加成功');
});

const updateFun = useBaseAsync(async(data: BusinessRoleAdminVo) => {
  await useApi().businessRoleApi.updateBusinessRole(data);
  ElMessage.success('修改成功');
});

const deleteFun = useBaseAsync(async(data: BusinessRoleAdminVo) => {
  await useApi().businessRoleApi.delBusinessRole(data.id!);
  ElMessage.success('删除成功');
});

init();
</script>
